import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { PERMISSION } from 'constants/logic';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectUserHasPermission } from 'selectors/general';

import { actionsMessages } from '../ScheduleDetail/ScheduleDetail.messages';
import {
  cancelSchedule,
  emailScheduleAttendees,
  openCancelScheduleModal,
  redirectToManageClass,
  redirectToManageSchedule,
} from '../YieldManagement.saga';
import { countReservations, getActionWindows } from './ScheduleDetailV2.helpers';
import { useScheduleRoster } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';

const SEGMENT_EMAIL_ATTENDEES_CLICKED = 'Email attendees clicked';

export function useEmailActionItems(schedule) {
  const dispatch = useDispatch();
  const { venueId, venueName, venueSubtitle } = useSelector(selectActiveVenue);
  const { data: roster } = useScheduleRoster(schedule.scheduleId);

  const { canPerformScheduleActions, isInExtendedActionsWindow } = getActionWindows(schedule);
  const { newReservationsCount, reservationsCount } = countReservations(roster);

  const visible = !schedule.isDisabled && (canPerformScheduleActions || isInExtendedActionsWindow);
  const frosted = reservationsCount === 0;

  const openEmailModal = ({ newAttendeesOnly, reservations, title }) => {
    Segment.event(SEGMENT_EMAIL_ATTENDEES_CLICKED, { venueId, scheduleId: schedule.scheduleId, reservationsCount });
    dispatch(
      openModal('EmailModal', {
        onSubmit: (...args) => dispatch(emailScheduleAttendees(...args)),
        title,
        to: <Message message={actionsMessages.toContent} values={{ reservations }} />,
        subject: (
          <Message
            message={actionsMessages.subjectContent}
            values={{
              startDateTime: schedule.startDateTime,
              venueNameSubtitle: `${venueName}${venueSubtitle ? ` (${venueSubtitle})` : ''}`,
            }}
          />
        ),
        initialValues: {
          schedule,
          newAttendeesOnly,
          message: '',
        },
      })
    );
  };

  const items = visible
    ? [
        {
          key: 'email-class',
          label: <Message message={messages.emailClass} />,
          onSelect: () =>
            openEmailModal({
              newAttendeesOnly: false,
              reservations: reservationsCount,
              title: <Message message={messages.emailClass} />,
            }),
        },
        newReservationsCount > 0 &&
          !isInExtendedActionsWindow && {
            key: 'email-new-clients',
            label: <Message message={messages.emailNewClients} values={{ count: newReservationsCount }} />,
            onSelect: () =>
              openEmailModal({
                newAttendeesOnly: true,
                reservations: newReservationsCount,
                title: <Message message={messages.emailNewClients} values={{ count: newReservationsCount }} />,
              }),
          },
      ].filter(Boolean)
    : [];

  return { frosted, items, visible };
}

export function useEditActionItems(schedule) {
  const dispatch = useDispatch();
  const { isMsInventoryVenue, venueId } = useSelector(selectActiveVenue);
  const shouldShowCancelRecurring = useSelector(state =>
    selectUserHasPermission(state, PERMISSION.CAN_CANCEL_ALL_FUTURE)
  );
  const cannotCancelClass = useSelector(state => selectUserHasPermission(state, PERMISSION.REMOVE_CLASS_CANCEL));

  const { canPerformScheduleActions, isInExtendedActionsWindow } = getActionWindows(schedule);
  const visible = canPerformScheduleActions || isInExtendedActionsWindow;

  const { apiGuid, isCancellable, isDisabled, isInThePast, scheduleClass, startDate, startDateTime } = schedule;
  const canCancel = !cannotCancelClass && isCancellable && !isDisabled;
  const dispatchCancelSchedule = (...args) => dispatch(cancelSchedule(...args));

  const items = visible
    ? [
        {
          key: 'edit-class-details',
          label: <Message message={messages.editClassDetails} />,
          onSelect: () => dispatch(redirectToManageClass(venueId, scheduleClass?.classId)),
        },
        !isInExtendedActionsWindow &&
          !isInThePast &&
          isMsInventoryVenue &&
          !isDisabled && {
            key: 'edit-staff',
            label: <Message message={messages.editStaff} />,
            onSelect: () => dispatch(redirectToManageSchedule({ apiGuid, startDate, startDateTime, venueId })),
          },
        canCancel && {
          danger: true,
          key: 'cancel-class',
          label: <Message message={messages.cancelClass} />,
          onSelect: () =>
            dispatch(
              openCancelScheduleModal({
                activeSchedule: schedule,
                cancelSchedule: dispatchCancelSchedule,
                isMsInventoryVenue,
                isRecurring: false,
              })
            ),
        },
        canCancel &&
          !isMsInventoryVenue &&
          shouldShowCancelRecurring && {
            danger: true,
            key: 'cancel-all-future',
            label: <Message message={messages.cancelAllFuture} />,
            onSelect: () =>
              dispatch(
                openModal('CancelScheduleModal', {
                  activeSchedule: schedule,
                  cancelSchedule: dispatchCancelSchedule,
                  isRecurring: true,
                })
              ),
          },
      ].filter(Boolean)
    : [];

  return { items, visible };
}
